// Lib
import React, { ReactElement, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Selector
import { getIsFeatureEnabledForCurrentUser } from '../../feature/elementFeatureSelector';

// Actions
import { updateElementIfNotDeleted, updateElementContentDiff } from '../../actions/elementActions';

// Hooks
import useCardTiptapEditorExtensions from '../../card/tiptap/useCardTiptapEditorExtensions';
import useClientElementTiptapEditor from '../../../components/tiptapEditor/useClientElementTiptapEditor';
import { useSuggestionsManager } from '../../../components/tiptapEditor/useSuggestionsManager';
import useKeepModalEditorScrolledIntoView from './useKeepModalEditorScrolledIntoView';

// Components
import { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import { ExperimentId } from '../../../../common/experiments/experimentsConstants';

// Types
import { EditorContent } from '@tiptap/react';
import { Rect } from '../../../../common/maths/geometry/rect/rectTypes';
import { useTranslator } from '../../../components/i18n/useTranslator';
import { SaveTiptapEditorContentFunction } from '../../../../common/tiptap/extensions/externalStoreSync/externalStoreSynchroniserTypes';

interface DocumentModalTiptapEditorProps {
    elementId: string;
    textContent: TiptapContent | null;
    editorId: string;
    editorKey: string;
    currentEditorId: string;
    isEditable: boolean;
    isEditing: boolean;
    dispatchStartEditing: (args: { editorId: string; editorKey: string; editorFocusRect?: Rect }) => void;
    dispatchStartEditingDocument: () => void;
    isMobile?: boolean;
}

const DocumentModalTiptapEditor = (props: DocumentModalTiptapEditorProps): ReactElement => {
    const {
        elementId,
        textContent,
        isEditable,
        editorId,
        editorKey,
        currentEditorId,
        isEditing,
        dispatchStartEditing,
        isMobile,
    } = props;

    const isDocumentDiffSyncingEnabled = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.documentDiffSyncing),
    );
    const dispatch = useDispatch();

    const dispatchSaveContent: SaveTiptapEditorContentFunction = useCallback(
        (textContent, actionArgs) => {
            isDocumentDiffSyncingEnabled
                ? dispatch(
                      updateElementContentDiff({
                          id: elementId,
                          changes: { textContent },
                          silent: false,
                          ...actionArgs,
                      }),
                  )
                : dispatch(updateElementIfNotDeleted({ id: elementId, changes: { textContent }, ...actionArgs }));
        },
        [dispatch, elementId, isDocumentDiffSyncingEnabled],
    );

    const translate = useTranslator('elements', 'elementTypes.document');

    const [suggestionsManager, mentionsPopup] = useSuggestionsManager();
    const extensions = useCardTiptapEditorExtensions({
        suggestionsManager,
        isEditable,
        dispatch,
        saveContent: dispatchSaveContent,
        clickableLinksWhenEditable: true,
        universalCardConversion: false,
        elementId,
        translate,
    });

    const { editor, onMouseDown, onClick } = useClientElementTiptapEditor({
        persistedContent: textContent,
        extensions,

        editorId,
        editorKey,
        currentEditorId,

        isEditing,
        isEditable,
        isSingleSelected: true,

        startEditing: dispatchStartEditing,
        saveContent: dispatchSaveContent,

        editorClassname: 'clickable-links-on-edit',
    });

    useKeepModalEditorScrolledIntoView(editor, isEditing, isMobile);

    return (
        <div className="DocumentModalTiptapEditorContainer" onMouseDown={onMouseDown} onClick={onClick}>
            {mentionsPopup}
            <EditorContent className="DocumentModalTiptapEditor" dir="auto" editor={editor} />
        </div>
    );
};

export default DocumentModalTiptapEditor;
